import { useState, useEffect, useCallback } from 'react'
import { FileText, Upload, Trash2, Download, Search } from 'lucide-react'
import { documentApi } from '../services/documentApi'
import { projectApi } from '../services/projectApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { EmptyState } from '../components/common/EmptyState'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import type { Document as DocType, Project } from '../types'

export function Documents() {
  const [documents, setDocuments] = useState<DocType[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterProject, setFilterProject] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [uploadProjectId, setUploadProjectId] = useState('')

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await documentApi.getByProject(filterProject || undefined)
      setDocuments(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents')
    } finally {
      setLoading(false)
    }
  }, [filterProject])

  useEffect(() => { loadDocuments() }, [loadDocuments])

  useEffect(() => {
    projectApi.getAll({}).then((result) => setProjects(result.items)).catch(() => {})
  }, [])

  async function handleUpload(files: FileList | null) {
    if (!files) return
    const projectId = uploadProjectId || filterProject
    if (!projectId) {
      setError('Please select a project before uploading.')
      return
    }
    try {
      setUploading(true)
      setError(null)
      for (const file of Array.from(files)) {
        await documentApi.upload(projectId, file)
      }
      loadDocuments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  async function handleDownload(doc: DocType) {
    try {
      const blob = await documentApi.download(doc.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download file')
    }
  }

  async function handleDelete() {
    if (!deleteId) return
    try {
      await documentApi.delete(deleteId)
      setDeleteId(null)
      loadDocuments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document')
    }
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const filteredDocs = searchTerm
    ? documents.filter((d) => d.fileName.toLowerCase().includes(searchTerm.toLowerCase()))
    : documents

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Documents</h1>
          <p className="page-subtitle">Project files and attachments</p>
        </div>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      <div className="card mb-6">
        <div className="card-body">
          <div className="d-flex align-center gap-4 mb-4">
            <div className="form-group mb-0" style={{ minWidth: '240px' }}>
              <label className="text-xs">Upload to project</label>
              <select className="form-select form-select-sm" value={uploadProjectId || filterProject} onChange={(e) => setUploadProjectId(e.target.value)}>
                <option value="">Select project...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div
            className={`upload-zone${dragOver ? ' drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files) }}
          >
            <Upload size={32} className="text-muted" />
            {uploading ? (
              <p className="text-secondary-color">Uploading...</p>
            ) : (
              <p>
                Drag and drop files here, or{' '}
                <label className="upload-browse-label">
                  <input type="file" multiple hidden onChange={(e) => handleUpload(e.target.files)} />
                  <span className="text-accent cursor-pointer">browse</span>
                </label>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="input-group" style={{ flex: '1', maxWidth: '300px' }}>
          <Search size={16} className="input-icon" />
          <input className="form-input search-input" placeholder="Search files..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <select className="form-select" value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
          <option value="">All Projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading ? (
        <LoadingSpinner message="Loading documents..." />
      ) : filteredDocs.length === 0 ? (
        <EmptyState
          icon={<FileText size={48} />}
          title="No documents found"
          message={searchTerm || filterProject ? 'Try adjusting your search or filter.' : 'Upload documents to your projects using the area above.'}
        />
      ) : (
        <div className="table-container">
          <div className="table-summary">{filteredDocs.length} file{filteredDocs.length !== 1 ? 's' : ''}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Project</th>
                <th>Category</th>
                <th>Size</th>
                <th>Uploaded By</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <div className="d-flex align-center gap-2">
                      <FileText size={16} className="text-muted flex-shrink-0" />
                      <span className="font-medium">{doc.fileName}</span>
                    </div>
                  </td>
                  <td className="text-secondary-color">{doc.projectName}</td>
                  <td className="text-secondary-color">{doc.category || '-'}</td>
                  <td className="text-secondary-color text-nowrap">{formatSize(doc.sizeBytes)}</td>
                  <td className="text-secondary-color">{doc.uploadedBy}</td>
                  <td className="text-secondary-color text-nowrap">{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-xs btn-icon" onClick={() => handleDownload(doc)} title="Download"><Download size={14} /></button>
                      <button className="btn btn-ghost btn-xs btn-icon text-danger" onClick={() => setDeleteId(doc.id)} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        title="Delete Document"
        message="Are you sure you want to delete this document? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  )
}
